import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Chip, Colors, Container, Text} from "@bounce/shared/ui";
import {Button} from "../../ui/components";
import {useGetSegmentTemplates} from "../../apis/requests/segments/getSegmentTemplates";

type Props = {
  segmentId: string;
  segmentName: string;
  onConfirm: () => void;
};

const DeleteSegmentDialog = NiceModal.create(({segmentId, segmentName, onConfirm}: Props) => {
  const modal = useModal();
  const {data: templates, isLoading} = useGetSegmentTemplates({id: segmentId});
  const attachedTemplates = templates ?? [];

  const handleConfirm = () => {
    onConfirm();
    modal.hide();
  };

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.hide()}
      slotProps={{
        transition: {onExited: () => modal.remove()},
        paper: {sx: {borderRadius: "16px"}},
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{p: 3, pb: 0}}>
        <Text size={24} variant="interDisplay600">
          Delete segment
        </Text>
      </DialogTitle>
      <DialogContent sx={{p: 3}}>
        <Container fullWidth sx={{gap: 2, mt: 1, alignItems: "flex-start"}}>
          <Text size={14} variant="inter400" sx={{color: Colors.grey._900, textAlign: "left"}}>
            &quot;{segmentName}&quot; will be deleted when you save all changes.
          </Text>
          {isLoading ? (
            <Box sx={{display: "flex", justifyContent: "center", width: "100%", py: 2}}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            attachedTemplates.length > 0 && (
              <Container fullWidth sx={{gap: 1, alignItems: "flex-start"}}>
                <Text size={13} variant="inter400" sx={{color: Colors.grey._700, textAlign: "left"}}>
                  It is attached to {attachedTemplates.length} template
                  {attachedTemplates.length > 1 ? "s" : ""} and will be removed from them:
                </Text>
                <Container row fullWidth sx={{gap: 1, flexWrap: "wrap", justifyContent: "flex-start"}}>
                  {attachedTemplates.map((name) => (
                    <Chip key={name} size_="small" variant_="inherit" label={name} />
                  ))}
                </Container>
              </Container>
            )
          )}
        </Container>
      </DialogContent>
      <DialogActions sx={{p: 3, pt: 0, justifyContent: "flex-end", gap: 1}}>
        <Button buttonType="outlined-secondary" size="small" onClick={() => modal.hide()}>
          Cancel
        </Button>
        <Button buttonType="contained-error" size="small" onClick={handleConfirm}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default DeleteSegmentDialog;
